import { type ComponentProps, lazy, Suspense, useMemo, useState } from 'react';

import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';

import { assistCardTitles, chipDuplicatesCard } from '../../lib/chip-dedupe';
import {
  getVisibleRecommendedActions,
  NextStepsTitle,
  RecommendedActions,
  recommendedActionsTitle,
} from '../RecommendedActions';

/**
 * Loaded on demand: the card pulls in the setup panel's steps, the note editor and its markdown preview,
 * none of which the first page needs (the initial-bundle budget, ACT2-1008). Only a turn with a card loads it.
 */
const AssistSuggestionMessage = lazy(() =>
  import('./AssistSuggestionMessage.js').then((m) => ({ default: m.AssistSuggestionMessage })),
);

interface NextStepsBlockProps {
  /**
   * The turn's `assist_suggestion` ui_messages. `id` is the stored seq, so a card's state (typed note text,
   * a dismissal) survives an older card being inserted above it.
   */
  cards: { id: string; content: string }[];
  /** The turn's follow-up chips while they are shown; absent, the cards stand alone in the same frame. */
  chips?: Omit<ComponentProps<typeof RecommendedActions>, 'embedded' | 'isDuplicate'>;
}

/**
 * A turn's "Next steps": its assist cards as the top rows, then the follow-up chips, in one bordered
 * block, so the two never fill the viewport between them. The cards keep their place (and what was typed
 * in them) when the chips come or go, and a block whose cards were all dismissed and has no chips
 * renders nothing. The title is the chips' own, so a block of cards alone has none. A chip that says
 * nearly what a card says ("Save 'active account' definition" under "Save what 'active account' means")
 * is not drawn while that card is on screen: assist off or the card dismissed, the chip comes back. The
 * title shows only above chips that are drawn, so the block never shows a title with nothing under it.
 */
export function NextStepsBlock({ cards, chips }: NextStepsBlockProps): React.ReactElement {
  const assistOn = useWorkspaceOrNull()?.workspace.assistEnabled === true;
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  // Only the cards on screen hide a chip: `AssistSuggestionMessage` draws nothing while assist is off.
  const cardTitles = useMemo(
    () =>
      assistOn ? cards.filter((card) => !dismissed.has(card.id)).flatMap((card) => assistCardTitles(card.content)) : [],
    [assistOn, cards, dismissed],
  );
  const isDuplicate = (label: string): boolean => chipDuplicatesCard(label, cardTitles);
  const chipsDrawn =
    chips !== undefined &&
    getVisibleRecommendedActions(chips.content, chips.dashboardSaved).some((action) => !isDuplicate(action.label));
  const title = chips && chipsDrawn ? recommendedActionsTitle(chips.metadata) : null;
  return (
    <div
      className="mx-6 my-2 animate-slide-up overflow-hidden rounded-xl border border-border bg-card empty:hidden"
      data-testid="next-steps"
    >
      {title && <NextStepsTitle>{title}</NextStepsTitle>}
      {cards.length > 0 && (
        <Suspense fallback={null}>
          {cards.map((card) => (
            <AssistSuggestionMessage
              key={card.id}
              content={card.content}
              onDismissed={() => setDismissed((current) => new Set(current).add(card.id))}
            />
          ))}
        </Suspense>
      )}
      {chips && <RecommendedActions {...chips} embedded isDuplicate={isDuplicate} />}
    </div>
  );
}
